import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import VisibilityPicker from "./VisibilityPicker";

/**
 * Iter-98 — quick "Add text note" dialog used inside Scene Detail,
 * Character Detail and Supervisor Notes page. Mirrors the shape of
 * `EditSupervisorNoteDialog` but issues `POST` to
 * `/projects/{pid}/supervisor-notes`. Scene/Character can be
 * preset (and locked) when invoked from a contextual page so the
 * created note auto-links to that scene/character.
 */
export default function AddSupervisorNoteDialog({
    open,
    onOpenChange,
    scenes = [],
    characters = [],
    presetSceneId = null,
    presetCharacterId = null,
    lockScene = false,
    lockCharacter = false,
    onCreate, // async ({text, scene_id, character_id, label}) => note
}) {
    const [text, setText] = useState("");
    const [sceneId, setSceneId] = useState("");
    const [characterId, setCharacterId] = useState("");
    const [label, setLabel] = useState("");
    // Iter-156B Phase 2 — visibility scope. Defaults to "everyone"
    // so the frictionless / muscle-memory case is unchanged.
    const [visibility, setVisibility] = useState("everyone");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setText("");
        setLabel("");
        setSceneId(presetSceneId || "");
        setCharacterId(presetCharacterId || "");
        setVisibility("everyone");
        setError("");
    }, [open, presetSceneId, presetCharacterId]);

    const submit = async () => {
        const trimmed = text.trim();
        if (!trimmed) {
            setError("Note text cannot be empty.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const payload = {
                text: trimmed,
                scene_id: sceneId || null,
                character_id: characterId || null,
                label: label || null,
                visibility,
            };
            await onCreate(payload);
            onOpenChange(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Save failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <DialogContent
                data-testid="add-supervisor-note-dialog"
                className="max-w-md bg-card border-violet-500/30"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-lg font-bold text-white">
                        Add text note
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Notes are reference only. They do not change scenes or characters.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 mt-2">
                    <div>
                        <Label
                            htmlFor="add-note-text"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Note
                        </Label>
                        <Textarea
                            id="add-note-text"
                            data-testid="add-supervisor-note-text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={4}
                            maxLength={4000}
                            placeholder="Type the note…"
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-y"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label
                                htmlFor="add-note-scene"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Scene
                            </Label>
                            <select
                                id="add-note-scene"
                                data-testid="add-supervisor-note-scene"
                                value={sceneId}
                                onChange={(e) => setSceneId(e.target.value)}
                                disabled={lockScene}
                                className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none disabled:opacity-70"
                            >
                                <option value="">— None —</option>
                                {scenes.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        Sc {s.scene_number || "?"}
                                        {s.heading ? ` · ${s.heading}` : ""}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label
                                htmlFor="add-note-char"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Character
                            </Label>
                            <select
                                id="add-note-char"
                                data-testid="add-supervisor-note-character"
                                value={characterId}
                                onChange={(e) => setCharacterId(e.target.value)}
                                disabled={lockCharacter}
                                className="mt-1.5 w-full h-10 px-3 rounded-md text-sm bg-secondary border-0 focus:ring-1 focus:ring-primary focus:outline-none disabled:opacity-70"
                            >
                                <option value="">— None —</option>
                                {characters.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label
                            htmlFor="add-note-label"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Category / source (optional)
                        </Label>
                        <Input
                            id="add-note-label"
                            data-testid="add-supervisor-note-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Day 12, Director note"
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>

                    {/* Iter-156B Phase 2 — visibility picker. Defaults to
                        Project Team so the muscle-memory case is
                        unchanged. */}
                    <VisibilityPicker
                        value={visibility}
                        onChange={setVisibility}
                        testidPrefix="add-supervisor-note-visibility"
                    />

                    {error ? (
                        <p
                            data-testid="add-supervisor-note-error"
                            className="text-xs text-red-400"
                        >
                            {error}
                        </p>
                    ) : null}

                    <div className="flex gap-2 pt-2">
                        <Button
                            type="button"
                            data-testid="add-supervisor-note-cancel"
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={busy}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="add-supervisor-note-save"
                            onClick={submit}
                            disabled={busy}
                            className="flex-1 min-tap"
                        >
                            {busy ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                "Add note"
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
